import React, { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import closeIcon from 'assets/filters/close.svg';
import VectorIcon from 'assets/myLeads/vector.svg';
import Button from 'common-ui/button';
import CustomDatePicker from 'common-ui/customDatePicker';
import Input from 'common-ui/input';
import ResetButton from 'common-ui/resetButton';
import { AsyncSelect, InfiniteAsyncSelect, Select } from 'common-ui/select';
import dayjs from 'dayjs';
import { selectUserType } from 'features/auth/authSlice';
import { getLeadSources, getWorkflowStatuses } from 'features/newsales/newsalesActions';
import { getUsersDropdown } from 'features/users/usersActions';
import useDebounce from 'hooks/useDebounce';

import { VIVA_OPTIONS, WITH_APPOINTMENT_OPTIONS } from '../../MyLeadConstants';
import {
  AssignButton,
  BulkActionsWrapper,
  ClearAllRow,
  ClearAllText,
  CloseIconWrapper,
  CustomDatePickerWrapper,
  DateRangeGroup,
  FilterClearRow,
  FilterContainer,
  FilterLabel,
  FilterRowLine,
  Icon,
  InputWrapper,
  Line,
  MainActionsRow,
  ReAssignButton,
  TakeLeadButton,
} from './SearchAndFilter.styles';

const STATUS_FILTER_OPTIONS = [
  { value: 'assigned', label: 'Assigned' },
  { value: 'unassigned', label: 'Unassigned' },
];

const getSearchAndFilterCount = (searchData) => {
  const searchKeys = ['fullName', 'companyName', 'communicationMethod', 'leadNumber'];
  const filterKeys = [
    'businessType',
    'createdDateFrom',
    'createdDateTo',
    'workflowStatusId',
    'status',
    'leadSourceId',
    'createdByUuid',
    'assigneeUuid',
    'withAppointment',
    'type',
  ];
  const searchCount = searchKeys.reduce(
    (count, key) => count + (searchData[key] && String(searchData[key]).trim() !== '' ? 1 : 0),
    0
  );
  const filterCount = filterKeys.reduce(
    (count, key) => count + (searchData[key] && String(searchData[key]).trim() !== '' ? 1 : 0),
    0
  );
  return { searchCount, filterCount };
};

const SearchAndFilter = ({
  searchData,
  setSearchData,
  selectedLeads = [],
  onAssign,
  onReAssign,
  onTakeLead,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userType = useSelector(selectUserType);

  const ASSIGN_ROLES = [
    'Super Admin',
    'General Manager',
    'Department Head',
    'Branch Head',
    'Team Lead',
  ];
  const canAssign = ASSIGN_ROLES.includes(userType);

  const { searchCount, filterCount } = getSearchAndFilterCount(searchData);

  const [showFilters, setShowFilters] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const [first, setFirst] = useState(false);
  const [fullNameInput, setFullNameInput] = useState(searchData.fullName || '');
  const [companyNameInput, setCompanyNameInput] = useState(searchData.companyName || '');
  const [communicationMethodInput, setCommunicationMethodInput] = useState(
    searchData.communicationMethod || ''
  );
  const [leadNumberInput, setLeadNumberInput] = useState(searchData.leadNumber || '');

  const [selectedWorkflowStatus, setSelectedWorkflowStatus] = useState(null);
  const [selectedLeadSource, setSelectedLeadSource] = useState(null);
  const [selectedCreatedByUser, setSelectedCreatedByUser] = useState(null);
  const [selectedAssignedToUser, setSelectedAssignedToUser] = useState(null);

  const debouncedFullName = useDebounce(fullNameInput, 500);
  const debouncedCompanyName = useDebounce(companyNameInput, 500);
  const debouncedCommunicationMethod = useDebounce(communicationMethodInput, 500);
  const debouncedLeadNumber = useDebounce(leadNumberInput, 500);

  useEffect(() => {
    if (first) setSearchData({ fullName: debouncedFullName });
  }, [debouncedFullName]);

  useEffect(() => {
    if (first) setSearchData({ companyName: debouncedCompanyName });
  }, [debouncedCompanyName]);

  useEffect(() => {
    if (first) setSearchData({ communicationMethod: debouncedCommunicationMethod });
  }, [debouncedCommunicationMethod]);

  useEffect(() => {
    if (first) setSearchData({ leadNumber: debouncedLeadNumber });
  }, [debouncedLeadNumber]);

  const handleChange = (type, value) => {
    setFirst(true);
    if (type === 'fullName') setFullNameInput(value);
    else if (type === 'companyName') setCompanyNameInput(value);
    else if (type === 'communicationMethod') setCommunicationMethodInput(value);
    else if (type === 'leadNumber') setLeadNumberInput(value);
  };

  const loadWorkflowStatusesAction = useCallback(
    async (params) => {
      try {
        const res = await dispatch(getWorkflowStatuses(params)).unwrap();
        if (
          !params.search &&
          !params.offset &&
          searchData.workflowStatusId &&
          !selectedWorkflowStatus
        ) {
          const found = (res.data || []).find(
            (s) => String(s.uuid || s.id) === String(searchData.workflowStatusId)
          );
          if (found)
            setSelectedWorkflowStatus({
              value: String(found.uuid || found.id),
              label: found.name,
            });
        }
        return res;
      } catch {
        return { data: [], total: 0 };
      }
    },
    [dispatch, searchData.workflowStatusId, selectedWorkflowStatus]
  );

  const loadLeadSourcesAction = useCallback(
    async (params) => {
      try {
        const res = await dispatch(getLeadSources(params)).unwrap();
        if (
          !params.search &&
          !params.offset &&
          searchData.leadSourceId &&
          !selectedLeadSource
        ) {
          const found = (res.data || []).find(
            (s) => String(s.uuid || s.id) === String(searchData.leadSourceId)
          );
          if (found)
            setSelectedLeadSource({
              value: String(found.uuid || found.id),
              label: found.name,
            });
        }
        return res;
      } catch {
        return { data: [], total: 0 };
      }
    },
    [dispatch, searchData.leadSourceId, selectedLeadSource]
  );

  const loadUsersAction = useCallback(
    async (params) => {
      try {
        const response = await dispatch(getUsersDropdown(params)).unwrap();
        const users = (response.users || []).map((user) => ({
          ...user,
          name:
            user.name && user.surname
              ? `${user.name} ${user.surname}`
              : user.name || user.surname || user.email,
        }));
        if (!params.search && !params.offset) {
          if (searchData.createdByUuid && !selectedCreatedByUser) {
            const found = users.find((u) => (u.uuid || u.id) === searchData.createdByUuid);
            if (found)
              setSelectedCreatedByUser({ value: found.uuid || found.id, label: found.name });
          }
          if (searchData.assigneeUuid && !selectedAssignedToUser) {
            const found = users.find((u) => (u.uuid || u.id) === searchData.assigneeUuid);
            if (found)
              setSelectedAssignedToUser({ value: found.uuid || found.id, label: found.name });
          }
        }
        return { ...response, users };
      } catch {
        return { users: [], count: 0 };
      }
    },
    [
      dispatch,
      searchData.createdByUuid,
      searchData.assigneeUuid,
      selectedCreatedByUser,
      selectedAssignedToUser,
    ]
  );

  useEffect(() => {
    if (searchData.workflowStatusId) loadWorkflowStatusesAction({ limit: 10, offset: 0 });
    if (searchData.leadSourceId) loadLeadSourcesAction({ limit: 10, offset: 0 });
    if (searchData.createdByUuid || searchData.assigneeUuid)
      loadUsersAction({ limit: 10, offset: 0 });
  }, []);

  const handleFilterChange = (field, value) => {
    setSearchData({ [field]: value || '' });
  };

  const handleClearFilters = () => {
    setSelectedWorkflowStatus(null);
    setSelectedLeadSource(null);
    setSelectedCreatedByUser(null);
    setSelectedAssignedToUser(null);
    setSearchData({
      createdDateFrom: '',
      createdDateTo: '',
      workflowStatusId: '',
      status: '',
      leadSourceId: '',
      createdByUuid: '',
      assigneeUuid: '',
      withAppointment: '',
      type: '',
    });
  };

  const handleReset = () => {
    setFullNameInput('');
    setCompanyNameInput('');
    setCommunicationMethodInput('');
    setLeadNumberInput('');
    setSelectedWorkflowStatus(null);
    setSelectedLeadSource(null);
    setSelectedCreatedByUser(null);
    setSelectedAssignedToUser(null);
    setSearchData({
      leadNumber: '',
      businessType: '',
      fullName: '',
      companyName: '',
      communicationMethod: '',
      createdDateFrom: '',
      createdDateTo: '',
      workflowStatusId: '',
      status: '',
      leadSourceId: '',
      createdByUuid: '',
      assigneeUuid: '',
      withAppointment: '',
      type: '',
    });
  };

  const handleCloseSearch = () => {
    setShowSearch(false);
    setFullNameInput('');
    setCompanyNameInput('');
    setCommunicationMethodInput('');
    setLeadNumberInput('');
    setSearchData({ leadNumber: '', fullName: '', companyName: '', communicationMethod: '' });
  };

  return (
    <FilterContainer>
      <MainActionsRow>
        <div className="actions-left">
          <Button
            outlined
            onClick={() => {
              setShowSearch(!showSearch);
              setShowFilters(false);
            }}
            active={searchCount > 0}
            variant="search"
            className="action-button"
          >
            Search{searchCount > 0 ? ` (${searchCount})` : ''}
          </Button>

          <Button
            outlined
            onClick={() => {
              setShowFilters(!showFilters);
              setShowSearch(false);
            }}
            active={filterCount > 0}
            variant="filter"
            className="action-button"
          >
            Filter{filterCount > 0 ? ` (${filterCount})` : ''}
          </Button>

          {(searchCount > 0 || filterCount > 0) && <ResetButton onClick={handleReset} />}
        </div>

        <Button
          width={180}
          secondary
          onClick={() => navigate('/sales-new')}
          className="create-lead-btn"
        >
          + Create Lead
        </Button>

        {selectedLeads.length > 0 && (
          <BulkActionsWrapper>
            {canAssign && selectedLeads.every((lead) => !lead.assigneeUuid) && (
              <AssignButton onClick={onAssign}>Assign</AssignButton>
            )}
            {canAssign && selectedLeads.every((lead) => !!lead.assigneeUuid) && (
              <ReAssignButton onClick={onReAssign}>Re Assign</ReAssignButton>
            )}
            {selectedLeads.every((lead) => !lead.assigneeUuid) && (
              <TakeLeadButton onClick={onTakeLead}>Take Lead</TakeLeadButton>
            )}
          </BulkActionsWrapper>
        )}
      </MainActionsRow>

      {showSearch && (
        <Line>
          <ClearAllRow>
            <CloseIconWrapper onClick={handleCloseSearch}>
              <Icon src={closeIcon} />
            </CloseIconWrapper>
          </ClearAllRow>
          <InputWrapper>
            <Input
              className="search-inputs"
              label="Lead ID"
              icon={focusedField === 'leadNumber' || leadNumberInput ? null : VectorIcon}
              value={leadNumberInput}
              onFocus={() => setFocusedField('leadNumber')}
              onBlur={() => setFocusedField(null)}
              onChange={(e) => handleChange('leadNumber', e.target.value)}
            />
            <Input
              className="search-inputs"
              label="Full Name"
              icon={focusedField === 'fullName' || fullNameInput ? null : VectorIcon}
              value={fullNameInput}
              onFocus={() => setFocusedField('fullName')}
              onBlur={() => setFocusedField(null)}
              onChange={(e) => handleChange('fullName', e.target.value)}
            />
            <Input
              className="search-inputs"
              label="Company Name"
              icon={focusedField === 'companyName' || companyNameInput ? null : VectorIcon}
              value={companyNameInput}
              onFocus={() => setFocusedField('companyName')}
              onBlur={() => setFocusedField(null)}
              onChange={(e) => handleChange('companyName', e.target.value)}
            />
            <Input
              className="search-inputs"
              label="Communication Method"
              icon={
                focusedField === 'communicationMethod' || communicationMethodInput
                  ? null
                  : VectorIcon
              }
              value={communicationMethodInput}
              onFocus={() => setFocusedField('communicationMethod')}
              onBlur={() => setFocusedField(null)}
              onChange={(e) => handleChange('communicationMethod', e.target.value)}
            />
          </InputWrapper>
        </Line>
      )}

      {showFilters && (
        <Line>
          <ClearAllRow>
            <CloseIconWrapper onClick={() => setShowFilters(false)}>
              <Icon src={closeIcon} />
            </CloseIconWrapper>
          </ClearAllRow>
          <FilterRowLine>
            <Select
              minHeight="38px"
              isClearable
              placeholder="Business model"
              options={['B2B', 'B2C']}
              value={
                searchData.businessType
                  ? { value: searchData.businessType, label: searchData.businessType }
                  : null
              }
              onChange={(option) =>
                setSearchData({ businessType: option ? option.value : '' })
              }
            />
            <DateRangeGroup className="date-group">
              <FilterLabel>Created Date</FilterLabel>
              <div className="date-inputs">
                <CustomDatePickerWrapper>
                  <CustomDatePicker
                    value={
                      searchData.createdDateFrom ? dayjs(searchData.createdDateFrom) : null
                    }
                    onChange={(date) =>
                      handleFilterChange(
                        'createdDateFrom',
                        date ? dayjs(date).format('YYYY-MM-DD') : ''
                      )
                    }
                    placeholder="From"
                    disableFuture
                    maxDate={searchData.createdDateTo ? dayjs(searchData.createdDateTo) : null}
                  />
                </CustomDatePickerWrapper>
                <CustomDatePickerWrapper>
                  <CustomDatePicker
                    value={searchData.createdDateTo ? dayjs(searchData.createdDateTo) : null}
                    onChange={(date) =>
                      handleFilterChange(
                        'createdDateTo',
                        date ? dayjs(date).format('YYYY-MM-DD') : ''
                      )
                    }
                    placeholder="To"
                    disableFuture
                    minDate={
                      searchData.createdDateFrom ? dayjs(searchData.createdDateFrom) : null
                    }
                  />
                </CustomDatePickerWrapper>
              </div>
            </DateRangeGroup>

            <InfiniteAsyncSelect
              minHeight="38px"
              loadOptionsAction={loadWorkflowStatusesAction}
              pageSize={10}
              paginationType="offset"
              value={selectedWorkflowStatus}
              onChange={(opt) => {
                setSelectedWorkflowStatus(opt);
                handleFilterChange('workflowStatusId', opt?.value || '');
              }}
              placeholder="Workflow Status"
              isClearable
              isSearchable
            />

            <AsyncSelect
              minHeight="38px"
              defaultOptions={STATUS_FILTER_OPTIONS}
              value={
                searchData.status
                  ? STATUS_FILTER_OPTIONS.find((o) => o.value === searchData.status) || null
                  : null
              }
              onChange={(opt) => handleFilterChange('status', opt?.value || '')}
              placeholder="Status"
              isClearable
              isSearchable={false}
            />

            <InfiniteAsyncSelect
              minHeight="38px"
              loadOptionsAction={loadLeadSourcesAction}
              pageSize={10}
              paginationType="offset"
              value={selectedLeadSource}
              onChange={(opt) => {
                setSelectedLeadSource(opt);
                handleFilterChange('leadSourceId', opt?.value || '');
              }}
              placeholder="Source"
              isClearable
              isSearchable
            />

            <InfiniteAsyncSelect
              minHeight="38px"
              loadOptionsAction={loadUsersAction}
              pageSize={10}
              paginationType="offset"
              value={selectedCreatedByUser}
              onChange={(opt) => {
                setSelectedCreatedByUser(opt);
                handleFilterChange('createdByUuid', opt?.value || '');
              }}
              placeholder="Created By"
              isClearable
              isSearchable
            />

            <InfiniteAsyncSelect
              minHeight="38px"
              loadOptionsAction={loadUsersAction}
              pageSize={10}
              paginationType="offset"
              value={selectedAssignedToUser}
              onChange={(opt) => {
                setSelectedAssignedToUser(opt);
                handleFilterChange('assigneeUuid', opt?.value || '');
              }}
              placeholder="Assigned To"
              isClearable
              isSearchable
            />

            <AsyncSelect
              minHeight="38px"
              defaultOptions={WITH_APPOINTMENT_OPTIONS}
              value={
                searchData.withAppointment
                  ? WITH_APPOINTMENT_OPTIONS.find(
                      (o) => o.value === searchData.withAppointment
                    ) || null
                  : null
              }
              onChange={(opt) => handleFilterChange('withAppointment', opt?.value || '')}
              placeholder="With Appointment"
              isClearable
              isSearchable={false}
            />

            <AsyncSelect
              minHeight="38px"
              defaultOptions={VIVA_OPTIONS}
              value={
                searchData.type
                  ? VIVA_OPTIONS.find((o) => o.value === searchData.type) || null
                  : null
              }
              onChange={(opt) => handleFilterChange('type', opt?.value || '')}
              placeholder="Viva"
              isClearable
              isSearchable={false}
            />
          </FilterRowLine>
          <FilterClearRow>
            <ClearAllText onClick={handleClearFilters}>Clear all</ClearAllText>
          </FilterClearRow>
        </Line>
      )}
    </FilterContainer>
  );
};

export default SearchAndFilter;
